import { Platform } from '@angular/cdk/platform';
// Import the core angular services.
import { Injectable, Inject, OnDestroy, ElementRef, Renderer2, RendererFactory2, NgZone } from '@angular/core';
import { Router, Scroll, Event } from '@angular/router';
import { LazyLoadService } from 'app/lazy-load/lazy-load.service';
import { NgScrollbar } from 'ngx-scrollbar';
import { of, Subject, Subscription, Observable, Observer, distinctUntilChanged, fromEvent } from 'rxjs';
import { auditTime, filter, pluck, startWith, pairwise, map, take, switchMap } from 'rxjs/operators';

/** Time in ms to throttle the scrolling events by default. */
export const DEFAULT_SCROLL_TIME = 20;

export interface ScrollbarDictionary {
  [key: string]: NgScrollbar;
}

// TODO: Add Angular decorator.
@Injectable({
  providedIn: 'root'
})
export class ScrollBarService implements OnDestroy {
  private renderer: Renderer2; // creato da factory
  scrollbars: Map<NgScrollbar, Subscription> = new Map();

  /** Subject for notifying that a registered scrollable reference element has been scrolled. */
  private _scrolled = new Subject<NgScrollbar | void>();
  /** Keeps track of the amount of subscriptions to `scrolled`. Used for cleaning up afterwards. */
  private _scrolledCount = 0;
  private _disableMonitor = false;

  public get mainScrollbar(): NgScrollbar {
    if (!this._mainScrollbar) {
      const mainScrollbar = this.getById('main-scrollbar');
      if (mainScrollbar) {
        this._mainScrollbar = mainScrollbar;
      }
    }
    return this._mainScrollbar;
  }
  private _mainScrollbar: NgScrollbar;

  constructor(
    private platform: Platform,
    private router: Router,
    rendererFactory: RendererFactory2,
    private lazyLoadService: LazyLoadService,
    private zone: NgZone
  ) {
    if (platform.isBrowser) {

      if ('onwheel' in document.createElement('div')) {
        fromEvent(document, 'wheel')
        .pipe(
          take(1),
          // https://github.com/gblazex/smoothscroll-for-websites v 1.4.10
          switchMap(() => this.lazyLoadService.loadScript('/lib/SmoothScroll.min.js', true))
        )
        .subscribe(() => {
          this.setSmoothScroll()
        });
      }

      this.renderer = rendererFactory.createRenderer(null, null);
      this.router.events.pipe(
        filter((e: Event): e is Scroll => e instanceof Scroll)
      ).subscribe(e => {
        // c'è un problema con la mainScrollbar, che sembra che non aggiorni passando da una pagina all'altra
        // se ad esempio passi dalla pagina login (aggiornata) alla home, non mostra più la scrollbar
        // setTimeout(() => {
        //   this.mainScrollbar.update();
        // }, 100);

        if (e.position) {
          // backward navigation
          this.mainScrollbar.scrollTo({ top: e.position[1], duration: 500 });
        } else if (e.anchor) {
          // anchor navigation
          const target = this.mainScrollbar.viewport.nativeElement.querySelector(`#${e.anchor}`);

          if (target) {
            this.mainScrollbar.scrollToElement(`#${e.anchor}`);
          }

        } else {
          // forward navigation
          this.mainScrollbar.viewport.nativeElement.scrollTop = 0;
        }
      });
    }
  }

  register(scrollable: NgScrollbar): void {
    if (!this.scrollbars.has(scrollable)) {
      // this.scrollbars.set(scrollable, scrollable.scrolled
      //   .subscribe(() => this._scrolled.next(scrollable)));
      this.scrollbars.set(scrollable, null);
    }
  }

  /**
   * Deregisters a Scrollable reference and unsubscribes from its scroll event observable.
   * @param ngScrollBar Scrollable instance to be deregistered.
   */
  deregister(scrollable: NgScrollbar): void {
    const scrollableReference = this.scrollbars.get(scrollable);

    if (scrollableReference) {
      scrollableReference.unsubscribe();
      this.scrollbars.delete(scrollable);
    }
  }

  scrolled(auditTimeInMs: number = DEFAULT_SCROLL_TIME): Observable<NgScrollbar | void> {
    if (this.platform.isBrowser) {
      return of(null);
    }

    return new Observable((observer: Observer<NgScrollbar | void>) => {
      // In the case of a 0ms delay, use an observable without auditTime
      // since it does add a perceptible delay in processing overhead.
      const subscription = auditTimeInMs > 0 ?
        this._scrolled.pipe(auditTime(auditTimeInMs)).subscribe(observer) :
        this._scrolled.subscribe(observer);

      this._scrolledCount++;

      return () => {
        subscription.unsubscribe();
        this._scrolledCount--;
      };
    });
  }

  ngOnDestroy(): void {
    this.scrollbars.forEach((_, container) => this.deregister(container));
    this._scrolled.complete();
  }

  /**
   * Returns an observable that emits whenever any of the
   * scrollable ancestors of an element are scrolled.
   * @param elementRef Element whose ancestors to listen for.
   * @param auditTimeInMs Time to throttle the scroll events.
   */
  ancestorScrolled(elementRef: ElementRef, auditTimeInMs?: number): Observable<NgScrollbar | void> {
    const ancestors = this.getAncestorScrollBars(elementRef);

    return this.scrolled(auditTimeInMs).pipe(filter(target => {
      return !target || ancestors.indexOf(target) > -1;
    }));
  }

  /** Returns all registered Scrollables that contain the provided element. */
  getAncestorScrollBars(elementRef: ElementRef): NgScrollbar[] {
    const scrollBars: NgScrollbar[] = [];

    this.scrollbars.forEach((_subscription: Subscription, scrollBar: NgScrollbar) => {
      if (this._scrollableContainsElement(scrollBar, elementRef)) {
        scrollBars.push(scrollBar);
      }
    });

    return scrollBars;
  }

  /** Returns true if the element is contained within the provided Scrollable. */
  private _scrollableContainsElement(scrollBar: NgScrollbar, elementRef: ElementRef): boolean {
    let element: HTMLElement | null = elementRef.nativeElement;
    const scrollableElement = scrollBar.nativeElement;

    // Traverse through the element parents until we reach null, checking if any of the elements
    // are the scrollable's element.
    do {
      if (element == scrollableElement) { return true; }
    } while (element = element!.parentElement);

    return false;
  }

  public getById(id: string): NgScrollbar {
    for (const sb of this.scrollbars.keys()) {
      if (sb.nativeElement.id === id) {
        return sb;
      }
    }
    return null;
  }

  public monitorScrollTop(scrollbar: NgScrollbar, ignorePageRestoring?: boolean): Observable<[any, any]> {
    return fromEvent(scrollbar.viewport.nativeElement, 'scroll')
      .pipe(
        startWith(0),
        filter(() =>  (this._disableMonitor && ignorePageRestoring) ? false : true),
        pluck('target', 'scrollTop'),
        pairwise()
      );
  }

  public reachedBottom(scrollbar?: NgScrollbar, offset = 0): Observable<boolean> {
    scrollbar = scrollbar ? scrollbar : this.mainScrollbar;
    return fromEvent(scrollbar.viewport.nativeElement, 'scroll')
      .pipe(
        map(({target}: any) => target.scrollTop + target.clientHeight >= target.scrollHeight - offset),
        distinctUntilChanged()
      );
  }

  public addScrollMonitor(id: string, sb: NgScrollbar): void {
    if (this.getById(id)) {
      throw new Error(`${id} was alrady be added to the monitor scroll service`);
    }
    this.scrollbars[id] = sb;
  }

  setSmoothScroll(customOptions?: any): void {
    if (window['SmoothScroll']) {
      let defaultOptions = {
        keyboardSupport: false,
        animationTime: 600, //400
        stepSize: 80, // 100
        accelerationDelta : 50,  // 50
        accelerationMax   : 3,   // 3
      };

      const opt = customOptions ? Object.assign(defaultOptions, customOptions) : defaultOptions
      this.zone.runOutsideAngular(() => {
        window['SmoothScroll'](opt);
      });

    }
  }

  public removeScrollMonitor(id: string): void {
    delete this.scrollbars[id];
  }
}
